/* eslint-disable no-unused-expressions */
import type { ChangeEvent, ReactElement } from 'react';
import {
  InputContainer,
  InputText,
  InputLabel,
  ErrorMessage,
  Accordion,
  InputEmail,
} from '@betrybe/ada-react';
import { SelectInput } from '@/components/molecules';
import { RadioInput } from '@/components/organisms';
import {
  cellPhoneMask,
  color,
  degreeOfKinshipOptions,
  hasIcon,
  maritalStatusOptions,
  monthOptions,
  nationalityOptions,
  phoneMask,
  propertyRegimeOptions,
  textColor,
} from '@/utils/index';
import { handleSetDay, handleSetYear } from '@/utils/setDateValidate';
import {
  checkAccordionChecked,
  checkAccordionErrors,
} from './checkAccordionErrors';

type BasicInformationFormTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: any;
  nationalityRef: React.LegacyRef<HTMLDivElement>;
  formType: 'candidate' | 'guarantor';
  degreeOfRelatednessRef?: React.LegacyRef<HTMLDivElement>;
  apiError?: boolean;
  setApiError?: React.Dispatch<React.SetStateAction<boolean>>;
};

function BasicInformationForm(props: BasicInformationFormTypes): ReactElement {
  const {
    formik,
    nationalityRef,
    formType,
    degreeOfRelatednessRef,
    apiError,
    setApiError,
  } = props;

  const accordionError: boolean = checkAccordionErrors({
    formik,
    formType,
    apiError,
  });
  const accordionChecked: boolean = checkAccordionChecked({ formik, formType });

  const textHeader = (
    <div className="flex flex-row space-x-2 items-center">
      {hasIcon({ accordionChecked, accordionError })}
      <p
        className={`bold text-base ${textColor({
          accordionChecked,
          accordionError,
        })}`}
      >
        Informações básicas
      </p>
    </div>
  );

  return (
    <Accordion
      header={textHeader}
      markerColor={color({ accordionChecked, accordionError })}
      defaultOpen
    >
      <div className="accordion-container space-y-5">
        <InputContainer
          label="Nome completo"
          error={formik.touched.name && formik.errors.name}
        >
          {(inputProps) => (
            <div>
              <p className="register-container__input-help-text">
                Preencha conforme o seu documento de identificação
              </p>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-fullName"
                  type="text"
                  name="name"
                  pattern="^([a-zA-Zà-úÀ-Ú0-9]|-|_|\s)+$"
                  placeholder="Meu nome é..."
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  data-testid="fullname"
                />
              </div>
            </div>
          )}
        </InputContainer>

        {formType === 'guarantor' && (
          <div
            className="register-container__filds-group"
            ref={degreeOfRelatednessRef}
          >
            <RadioInput
              options={degreeOfKinshipOptions()}
              label="Qual o seu grau de parentesco com essa pessoa? 👪"
              name="degree_of_relatedness"
              id="degree_of_relatedness"
              disabled={false}
              errorMessage={
                formik.touched.degree_of_relatedness &&
                formik.errors.degree_of_relatedness
              }
              helpText={undefined}
              onChange={formik.handleChange}
              selected={formik.values.degree_of_relatedness}
            />
            <InputContainer
              label=""
              error={
                formik.values.degree_of_relatedness === 'other' &&
                formik.touched.degree_of_relatedness_other &&
                formik.errors.degree_of_relatedness_other
              }
            >
              {(inputProps) => (
                <div>
                  <div>
                    <InputText
                      {...inputProps}
                      id="degree_of_relatedness_other"
                      type="text"
                      name="degree_of_relatedness_other"
                      placeholder="Primo ou prima"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={
                        formik.values.degree_of_relatedness === 'other'
                          ? formik.values.degree_of_relatedness_other
                          : ''
                      }
                      disabled={formik.values.degree_of_relatedness !== 'other'}
                    />
                  </div>
                </div>
              )}
            </InputContainer>
          </div>
        )}

        <SelectInput
          id="maritalStatus"
          label="Estado civil"
          onChange={formik.handleChange}
          placeholder="Pessoa solteira"
          value={formik.values.marital_status}
          name="marital_status"
          options={maritalStatusOptions.map(({ value, label }) => ({
            label,
            value,
          }))}
          errorMessage={
            formik.touched.marital_status && formik.errors.marital_status
              ? formik.errors.marital_status
              : ''
          }
          data-testid="maritalStatus"
        />

        {formType === 'guarantor' &&
          formik.values.marital_status === 'married' && (
            <InputContainer
              label="Regime de bens"
              error={
                formik.touched.marital_regime && formik.errors.marital_regime
              }
            >
              {(inputProps) => (
                <SelectInput
                  {...inputProps}
                  disabled={false}
                  id="marital_regime"
                  onChange={formik.handleChange}
                  placeholder="Comunhão Total de Bens"
                  value={formik.values.marital_regime}
                  name="marital_regime"
                  options={propertyRegimeOptions.map((regime) => ({
                    label: regime.label,
                    value: regime.value,
                  }))}
                  errorMessage=""
                />
              )}
            </InputContainer>
          )}

        {formType === 'guarantor' && (
          <>
            <div>
              <InputContainer
                label="E-mail"
                error={formik.touched.email && formik.errors.email}
              >
                {(inputProps) => (
                  <InputEmail
                    {...inputProps}
                    id="email"
                    name="email"
                    placeholder="exemplo@gmail.com"
                    onChange={(e) => {
                      formik.setFieldValue('email', e.target.value);
                      setApiError && setApiError(false);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    autoComplete="username"
                  />
                )}
              </InputContainer>
            </div>
            <InputContainer
              label="Telefone ou celular"
              error={formik.touched.phone_number && formik.errors.phone_number}
            >
              {(inputProps) => (
                <div>
                  <div>
                    <InputText
                      {...inputProps}
                      id="phone_number"
                      type="text"
                      name="phone_number"
                      placeholder="(11) 90000-0000"
                      onChange={(e) => {
                        let value;
                        if (e.target.value.length > 14) {
                          value = cellPhoneMask(e.target.value);
                        } else {
                          value = phoneMask(e.target.value);
                        }
                        formik.setFieldValue('phone_number', value);
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.phone_number}
                    />
                  </div>
                </div>
              )}
            </InputContainer>
          </>
        )}

        <div className="register-container__filds-group">
          <InputLabel>Data de nascimento</InputLabel>
          <div className="register-container__filds-group--inputs-row">
            <InputText
              id="input-register-day"
              type="number"
              name="day"
              placeholder="dia"
              max="31"
              min="01"
              maxLength={2}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (handleSetDay(e.target.value)) {
                  formik.setFieldValue('day', e.target.value);
                }
              }}
              value={formik.values.day}
              data-testid="day"
            />
            <SelectInput
              id="month"
              className="register-container__filds-group--month-fild"
              onChange={formik.handleChange}
              placeholder="mês"
              value={formik.values.month}
              name="month"
              options={monthOptions.map((month) => ({
                label: month.label,
                value: month.value,
              }))}
              errorMessage=""
              data-testid="month"
            />
            <InputText
              id="input-register-year"
              type="number"
              name="year"
              placeholder="ano"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (handleSetYear(e.target.value)) {
                  formik.setFieldValue('year', e.target.value);
                }
              }}
              onBlur={formik.handleBlur}
              value={formik.values.year}
              data-testid="year"
            />
          </div>
          <ErrorMessage>
            {((formik.touched.day && formik.errors.day) ||
              (formik.touched.month && formik.errors.month) ||
              (formik.touched.year && formik.errors.year)) && (
              <div>{formik.errors.day}</div>
            )}
          </ErrorMessage>
        </div>
        <div className="register-container__filds-group" ref={nationalityRef}>
          <InputLabel>Nacionalidade</InputLabel>
          <RadioInput
            options={nationalityOptions()}
            label=""
            name="nationality"
            id="nationality"
            disabled={false}
            errorMessage={
              formik.touched.nationality && formik.errors.nationality
            }
            helpText={undefined}
            onChange={formik.handleChange}
            selected={formik.values.nationality}
            data-testid="nationality"
          />
          <InputContainer
            label=""
            error={
              formik.values.nationality === 'outra' &&
              formik.touched.nationalityOther &&
              formik.errors.nationalityOther
            }
          >
            {(inputProps) => (
              <div>
                <div>
                  <InputText
                    {...inputProps}
                    id="input-register-nationalityOther"
                    type="text"
                    name="nationalityOther"
                    placeholder="Mexicana, Japonesa..."
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={
                      formik.values.nationality === 'brasileira'
                        ? ''
                        : formik.values.nationalityOther
                    }
                    disabled={
                      formik.values.nationality === 'brasileira' ||
                      formik.values.nationality === ''
                    }
                  />
                </div>
              </div>
            )}
          </InputContainer>
        </div>
        <InputContainer
          label="Local de nascimento"
          error={formik.touched.birthplace && formik.errors.birthplace}
        >
          {(inputProps) => (
            <div>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-birthPlace"
                  type="text"
                  name="birthplace"
                  placeholder="São Paulo, SP"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.birthplace}
                  data-testid="birthPlace"
                />
              </div>
            </div>
          )}
        </InputContainer>
      </div>
    </Accordion>
  );
}

BasicInformationForm.defaultProps = {
  degreeOfRelatednessRef: null,
  apiError: false,
  setApiError: undefined,
};

export default BasicInformationForm;
